import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  Group,
  LoadingOverlay,
  NumberInput,
  ScrollArea,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons";
import { UPDATE_LOAN_PRODUCT } from "apollo/mutuations";
import {
  ADMIN_LOAN_CATEGORIES,
  ADMIN_LOAN_PRODUCTS,
  LOAN_PROVIDERS,
} from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect } from "react";

const LoanProductEditModal = ({ setOpenedEdit, editData }) => {
  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      interest_type: "FLAT",
      interest_rate: 0,
      repayment_frequency: "MONTHLY",
      min_amount: 0,
      max_amount: 0,
      min_term: 1,
      max_term: 12,
      processing_fee_type: "PERCENT",
      processing_fee: 0,
      penalty_rate: 0,
      grace_period_days: 0,
      category_id: "",
      provider_id: "",
      allowed_terms: "",
    },
    validate: {
      name: (value) => (value ? null : "Name is required"),
      interest_rate: (value) =>
        value >= 0 ? null : "Interest rate must be 0 or greater",
      min_amount: (value) =>
        value >= 0 ? null : "Minimum amount must be 0 or greater",
      max_amount: (value, values) =>
        value >= values.min_amount
          ? null
          : "Maximum amount must be >= minimum amount",
      processing_fee: (value) =>
        value >= 0 ? null : "Processing fee must be 0 or greater",
    },
  });

  // Fetch loan providers
  const { data: providersData } = useQuery(LOAN_PROVIDERS, {
    variables: {
      first: 1000,
      page: 1,
      ordered_by: [{ column: "NAME", order: "ASC" }],
    },
  });

  // Fetch loan categories
  const { data: categoriesData } = useQuery(ADMIN_LOAN_CATEGORIES, {
    variables: {
      first: 1000,
      page: 1,
      orderBy: [{ column: "NAME", order: "ASC" }],
    },
  });

  useEffect(() => {
    if (editData) {
      form.setValues({
        name: editData.name || "",
        description: editData.description || "",
        interest_type: editData.interest_type || "FLAT",
        interest_rate: editData.interest_rate || 0,
        repayment_frequency: editData.repayment_frequency || "MONTHLY",
        min_amount: editData.min_amount || 0,
        max_amount: editData.max_amount || 0,
        min_term: editData.min_term || 1,
        max_term: editData.max_term || 12,
        processing_fee_type: editData.processing_fee_type || "PERCENT",
        processing_fee: editData.processing_fee || 0,
        penalty_rate: editData.penalty_rate || 0,
        grace_period_days: editData.grace_period_days || 0,
        category_id: editData.category?.id || "",
        provider_id: editData.provider?.id || "",
        allowed_terms: editData.allowed_terms || "",
      });
    }
  }, [editData]);

  const [updateLoanProduct, { loading }] = useMutation(UPDATE_LOAN_PRODUCT, {
    refetchQueries: [
      {
        query: ADMIN_LOAN_PRODUCTS,
      },
    ],
    onCompleted() {
      showNotification({
        color: "green",
        title: "Success",
        message: "Loan Product Updated Successfully",
      });
      setOpenedEdit(false);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Update Loan Product",
      });
    },
  });

  const handleSubmit = (values) => {
    updateLoanProduct({
      variables: {
        id: editData.id,
        name: values.name,
        description: values.description || null,
        interest_type: values.interest_type,
        interest_rate: parseFloat(values.interest_rate),
        repayment_frequency: values.repayment_frequency,
        min_amount: parseFloat(values.min_amount),
        max_amount: parseFloat(values.max_amount),
        min_term: parseInt(values.min_term),
        max_term: parseInt(values.max_term),
        processing_fee_type: values.processing_fee_type,
        processing_fee: parseFloat(values.processing_fee),
        penalty_rate: values.penalty_rate
          ? parseFloat(values.penalty_rate)
          : null,
        grace_period_days: values.grace_period_days
          ? parseInt(values.grace_period_days)
          : null,
        category_id: values.category_id || null,
        provider_id: values.provider_id || null,
        allowed_terms: values.allowed_terms || null,
      },
    });
  };

  const providerOptions =
    providersData?.loanProviders?.data?.map((provider) => ({
      value: provider.id,
      label: `${provider.name} (${provider.code})`,
    })) || [];

  const categoryOptions =
    categoriesData?.loanCategories?.data?.map((category) => ({
      value: category.id,
      label: category.name,
    })) || [];

  const interestTypeOptions = [
    { value: "FLAT", label: "Flat" },
    { value: "REDUCING", label: "Reducing Balance" },
  ];

  const repaymentFrequencyOptions = [
    { value: "DAILY", label: "Daily" },
    { value: "WEEKLY", label: "Weekly" },
    { value: "MONTHLY", label: "Monthly" },
    { value: "QUARTERLY", label: "Quarterly" },
  ];

  const processingFeeTypeOptions = [
    { value: "PERCENT", label: "Percentage" },
    { value: "FIXED", label: "Fixed Amount" },
  ];

  return (
    <div style={{ position: "relative", height: "75vh" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ margin: 0, marginBottom: "8px" }}>Edit Loan Product</h2>
        <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
          Update loan product configuration. All fields marked with * are
          required.
        </p>
      </div>
      <ScrollArea
        style={{ height: "calc(100% - 60px)" }}
        type="auto"
        offsetScrollbars
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Name"
                placeholder="Enter product name"
                required
                {...form.getInputProps("name")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <Select
                label="Loan Provider"
                placeholder="Select provider"
                data={providerOptions}
                searchable
                clearable
                {...form.getInputProps("provider_id")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <Textarea
                label="Description"
                placeholder="Enter description"
                minRows={2}
                {...form.getInputProps("description")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <Select
                label="Loan Category"
                placeholder="Select category"
                data={categoryOptions}
                searchable
                clearable
                {...form.getInputProps("category_id")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <Select
                label="Interest Type"
                placeholder="Select type"
                data={interestTypeOptions}
                {...form.getInputProps("interest_type")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Interest Rate (%)"
                placeholder="24.00"
                precision={2}
                min={0}
                required
                {...form.getInputProps("interest_rate")}
                mb="md"
                parser={(value) => value.replace(/[^\d.]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <Select
                label="Repayment Frequency"
                placeholder="Select frequency"
                data={repaymentFrequencyOptions}
                {...form.getInputProps("repayment_frequency")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Min Amount"
                placeholder="10000"
                min={0}
                hideControls
                required
                {...form.getInputProps("min_amount")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Max Amount"
                placeholder="500000"
                min={0}
                hideControls
                required
                {...form.getInputProps("max_amount")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Min Term (Months)"
                placeholder="1"
                min={1}
                {...form.getInputProps("min_term")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Max Term (Months)"
                placeholder="12"
                min={1}
                {...form.getInputProps("max_term")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <Select
                label="Processing Fee Type"
                placeholder="Select type"
                data={processingFeeTypeOptions}
                {...form.getInputProps("processing_fee_type")}
                mb="md"
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Processing Fee"
                placeholder="2.00"
                precision={2}
                min={0}
                required
                {...form.getInputProps("processing_fee")}
                mb="md"
                parser={(value) => value.replace(/[^\d.]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Penalty Rate (%)"
                placeholder="0.20"
                precision={2}
                min={0}
                {...form.getInputProps("penalty_rate")}
                mb="md"
                parser={(value) => value.replace(/[^\d.]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={3}>
              <NumberInput
                label="Grace Period (Days)"
                placeholder="3"
                min={0}
                {...form.getInputProps("grace_period_days")}
                mb="md"
                parser={(value) => value.replace(/[^\d]/g, "")}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <TextInput
                label="Allowed Terms"
                placeholder="1,2,3,6,12"
                {...form.getInputProps("allowed_terms")}
                mb="md"
                description="Comma-separated list of allowed term months"
              />
            </Grid.Col>
          </Grid>

          <Group
            position="right"
            mt="xl"
            mb="xl"
            style={{ paddingRight: "20px" }}
          >
            <Button
              type="submit"
              leftIcon={<IconDeviceFloppy size={16} />}
              style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
              disabled={loading}
            >
              Update
            </Button>
          </Group>
        </form>
      </ScrollArea>
    </div>
  );
};

export default LoanProductEditModal;
